import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { adminApi } from '@/api/endpoints'
import { ShieldIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { StatusPill, type Tone } from '@/components/ui/StatusPill'
import { LIVE_KEY } from '@/features/live/live'
import { cn, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { PracticeState, PracticeStatus } from '@/types/api'

const RESULT: Record<PracticeStatus, { label: string; tone: Tone }> = {
  starting: { label: 'Opening the page', tone: 'info' },
  waiting: { label: 'Waiting for you', tone: 'warning' },
  solved: { label: 'Cleared', tone: 'success' },
  missed: { label: 'Time ran out', tone: 'danger' },
  no_captcha: { label: 'No check shown', tone: 'neutral' },
  failed: { label: 'Could not open', tone: 'danger' },
}
const running = (state: PracticeState | null | undefined): boolean => state?.status === 'starting' || state?.status === 'waiting'

/** `34 s`, between two ISO times. */
function took(from: string | null, to: string | null): string | null {
  if (!from || !to) return null
  const seconds = Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 1000))
  return `${seconds} s`
}

/**
 * Try the whole path with a real CAPTCHA and no application: the worker opens
 * the vendor's own demo page, it comes up on the queue like a user's would,
 * and a person clears it. What it proves, end to end: the worker's browser,
 * the live picture, the clicks reaching the page, and the run noticing by
 * itself that the check is clear.
 */
export function PracticeCard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [site, setSite] = useState('recaptcha')
  const practice = useQuery({
    queryKey: ['practice'],
    queryFn: adminApi.practice,
    // Only worth asking often while one is on its way or waiting.
    refetchInterval: (query) => (running(query.state.data?.last) ? 1500 : false),
    staleTime: 0,
  })
  const last = practice.data?.last ?? null

  // The moment the page is parked, go to it: the clock is already running.
  const opened = useRef<string | null>(null)
  useEffect(() => {
    if (last?.status !== 'waiting' || opened.current === last.startedAt) return
    opened.current = last.startedAt
    void queryClient.invalidateQueries({ queryKey: LIVE_KEY })
    navigate(`/live/${encodeURIComponent(last.handoffId)}`)
  }, [last, navigate, queryClient])

  const start = useMutation({
    mutationFn: () => adminApi.startPractice(site),
    onSuccess: (state) => {
      opened.current = null
      queryClient.setQueryData(['practice'], { sites: practice.data?.sites ?? [], last: state })
      void practice.refetch()
    },
    onError: (error) => toast.error('Could not start a practice check', error instanceof ApiError ? error.message : undefined),
  })

  const sites = practice.data?.sites ?? []
  const busy = start.isPending || running(last)

  return (
    <section className="card mt-5 overflow-hidden">
      <div className="flex flex-col gap-4 px-4 py-4 sm:px-5 lg:flex-row lg:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-field bg-accent-soft text-ink-soft">
            <ShieldIcon className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-ink">Practice check</h2>
            <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">
              Opens a real CAPTCHA on its maker's demo page in the worker's browser. Clear it here to see the whole path work. No application is involved and nothing is submitted.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {sites.length > 1 && (
            <div role="radiogroup" aria-label="Which CAPTCHA" className="inline-flex rounded-field bg-surface-muted p-1">
              {sites.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={site === option.key}
                  disabled={busy}
                  onClick={() => setSite(option.key)}
                  className={cn('h-8 rounded-[8px] px-3 text-[13px] font-medium transition-colors', site === option.key ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink')}
                >
                  {option.label.replace(/^Google /, '')}
                </button>
              ))}
            </div>
          )}
          <Button size="sm" loading={busy} onClick={() => start.mutate()}>
            {running(last) ? (last?.status === 'waiting' ? 'Waiting for you' : 'Opening the page') : 'Start a practice check'}
          </Button>
        </div>
      </div>

      {last && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line bg-surface-muted/60 px-4 py-3 text-[12.5px] text-ink-muted sm:px-5">
          <StatusPill tone={RESULT[last.status].tone} live={running(last)} size="sm">
            {RESULT[last.status].label}
          </StatusPill>
          <span>
            {last.siteLabel} · {relativeTime(last.startedAt)}
            {last.status === 'solved' && took(last.waitingAt, last.endedAt) && <span className="num"> · cleared in {took(last.waitingAt, last.endedAt)}</span>}
          </span>
          {last.detail && <span className="min-w-0 basis-full text-ink-soft sm:basis-auto">{last.detail}</span>}
          {last.browser && <span className="min-w-0 basis-full text-ink-faint">{last.browser}</span>}
          {last.status === 'waiting' && (
            <button type="button" onClick={() => navigate(`/live/${encodeURIComponent(last.handoffId)}`)} className="font-medium text-info hover:underline">
              Open it
            </button>
          )}
        </div>
      )}
    </section>
  )
}
